import { ReactNode } from "react";
import t from "@/lang/lang";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import { ReferenceNumber } from "@/services/backend/documents/components/reference-number";
import { formatDate, formatTime } from "@/shared/lib/utilities/date";
import { TableCard } from "@/shared/components/tables/table-card";
import { TableLoadingRow } from "@/shared/components/tables/table-loading-row";
import { TableRtkErrorRow } from "@/shared/components/tables/table-rtk-error-row";
import { TableEmptyRow } from "@/shared/components/tables/table-empty-row";
import { PaginationLimitOffset } from "@/shared/components/tables/pagination-limit-offset";
import { DocumentStateBadge } from "@/shared/bsm/core/document/components/ui/document-state-badge";
import { ListRequest, useOmniContractListQuery } from "../api/contract-api";
import { Contract } from "../types/contract";
import { ContractStateBadge } from "./contract-state-badge";

/**
 * ContractTable lists the contracts of the request. actions are shown
 * below the table on the left, next to the pagination.
 */
export function ContractTable({
  request,
  onRequestChange,
  renderRowLink,
  actions,
}: {
  request: ListRequest;
  onRequestChange: (request: ListRequest) => void;
  renderRowLink: (contractId: string, children: ReactNode) => ReactNode;
  actions?: ReactNode;
}) {
  const { data: list, isLoading, error } = useOmniContractListQuery(request);

  const emptyList = list !== undefined && list.items.length === 0;

  return (
    <div className="grid gap-2">
      <TableCard>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("Nummer")}</TableHead>
              <TableHead>{t("Titel")}</TableHead>
              <TableHead>{t("Status")}</TableHead>
              <TableHead>{t("Vertragsstatus")}</TableHead>
              <TableHead>{t("Vertragsbeginn")}</TableHead>
              <TableHead>{t("Vertragsende")}</TableHead>
              <TableHead>{t("Aktualisiert am")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && <TableLoadingRow colSpan={7} />}
            {error && <TableRtkErrorRow error={error} colSpan={7} />}
            {emptyList && (
              <TableEmptyRow colSpan={7} text={t("Keine Verträge gefunden")} />
            )}
            {list?.items.map((contract) => (
              <ContractTableRow
                key={contract.id}
                contract={contract}
                renderRowLink={renderRowLink}
              />
            ))}
          </TableBody>
        </Table>
      </TableCard>
      <div className="flex items-center gap-2">
        {actions}
        {list && !emptyList && (
          <PaginationLimitOffset
            offset={request.offset ?? 0}
            limit={request.limit ?? 0}
            totalCount={list.totalCount}
            setLimitOffset={(limit, offset) =>
              onRequestChange({ ...request, limit, offset })
            }
            className="ml-auto"
          />
        )}
      </div>
    </div>
  );
}

function ContractTableRow({
  contract,
  renderRowLink,
}: {
  contract: Contract;
  renderRowLink: (contractId: string, children: ReactNode) => ReactNode;
}) {
  return (
    <TableRow>
      <TableCell>
        {renderRowLink(
          contract.id,
          <ReferenceNumber referenceNumber={contract.referenceNumber} />,
        )}
      </TableCell>
      <TableCell>{contract.title}</TableCell>
      <TableCell>
        <DocumentStateBadge document={contract} />
      </TableCell>
      <TableCell>
        <ContractStateBadge state={contract.contractState} />
      </TableCell>
      <TableCell>
        {contract.startDate && formatDate(contract.startDate)}
      </TableCell>
      <TableCell>{contract.endDate && formatDate(contract.endDate)}</TableCell>
      <TableCell className="whitespace-nowrap">
        {formatDate(contract.updatedAt)}, {formatTime(contract.updatedAt)}
      </TableCell>
    </TableRow>
  );
}
